'use client';
import { jsx } from '@emotion/react/jsx-runtime';
import { useState, useRef, useMemo } from 'react';
import { useTimeout } from '@s7/hooks';
import { HTTPError } from '@s7/http';
import { StatusCheckLinear, ArrowArrowRight } from '@s7/icons';
import { Link } from '@s7/ui';
import { useFooterAnalytics } from '../../analytics.js';
import { useSubscribeMutation, SubscriptionError } from '../../../domain/api/Subscription.js';
import { compileJsxLinkTemplate } from './utils/compileJsxLinkTemplate.js';
import { FooterSubscriptions } from '../../../../Footer/components/Subscriptions/FooterSubscriptions.js';
import { SubscriptionResponse } from '../../../domain/model/SubscriptionResponse.js';
import { emailRegExp } from './validators/email.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
var CATEGORY = "subscribe";
var SubscriptionsContainer = function(props) {
    var service = props.service, messages = props.messages, handlers = props.handlers, others = _object_without_properties_loose(props, [
        "service",
        "messages",
        "handlers"
    ]);
    var _ref = handlers || {}, onSuccess = _ref.onSuccess, onError = _ref.onError, onInvalid = _ref.onInvalid, onAlreadySubscribed = _ref.onAlreadySubscribed;
    var sendEvent = useFooterAnalytics();
    var dispatchErrorAnalyticsEvent = function(type, errorId, errorMessage) {
        return sendEvent("event_error", {
            item_category: CATEGORY,
            error: "submit_success",
            result: "subscribe_to_news",
            error_code: "code:" + errorId + ";text:" + errorMessage,
            type: type
        });
    };
    var dispatchSuccessAnalyticsEvent = function() {
        return sendEvent("submit_success", {
            item_category: CATEGORY,
            result: "subscribe_to_news"
        });
    };
    var _useState = useState("init"), state = _useState[0], setState = _useState[1];
    var _useState1 = useState(false), agreed = _useState1[0], setAgreed = _useState1[1];
    var _useState2 = useState(""), inputValue = _useState2[0], setInputValue = _useState2[1];
    var ignoreFocusRef = useRef(false);
    var textFieldRef = useRef(null);
    var returnFocus = function() {
        var _textFieldRef_current;
        ignoreFocusRef.current = true;
        (_textFieldRef_current = textFieldRef.current) == null ? void 0 : _textFieldRef_current.focus();
    };
    var _useTimeout = useTimeout(function() {
        setAgreed(false);
        setState("init");
        setTimeout(function() {
            return returnFocus();
        });
    }, 5000), startTimeout = _useTimeout.start;
    var handleResponse = function(data) {
        if (!data) {
            return;
        }
        setState("success");
        setInputValue("");
        dispatchSuccessAnalyticsEvent();
        startTimeout();
        onSuccess == null ? void 0 : onSuccess(data);
    };
    var handleError = function(error) {
        var _error_response;
        if (!error) {
            return;
        }
        var isExpectedValidationError = _instanceof(error, HTTPError) && [
            400,
            409
        ].includes(error == null ? void 0 : (_error_response = error.response) == null ? void 0 : _error_response.status);
        var isUnexpectedValidationError = _instanceof(error, SubscriptionError);
        if (isExpectedValidationError || isUnexpectedValidationError) {
            error.response.json().then(function(data) {
                var _data_errors;
                if (data == null ? void 0 : data.error) {
                    var _data_error, _data_error1;
                    dispatchErrorAnalyticsEvent("service", (_data_error = data.error) == null ? void 0 : _data_error.code, (_data_error1 = data.error) == null ? void 0 : _data_error1.message);
                }
                data == null ? void 0 : (_data_errors = data.errors) == null ? void 0 : _data_errors.forEach(function(error) {
                    dispatchErrorAnalyticsEvent("service", error.id, error.explain);
                });
                if (SubscriptionResponse.isAlreadySubscribed(data)) {
                    setState("alreadySubscribed");
                    onAlreadySubscribed == null ? void 0 : onAlreadySubscribed(data);
                } else if (SubscriptionResponse.isNotValid(data)) {
                    setState("pattern");
                    onInvalid == null ? void 0 : onInvalid(data);
                } else {
                    onError == null ? void 0 : onError();
                }
            });
        } else {
            setState("unexpected");
            dispatchErrorAnalyticsEvent("service", 500);
            onError == null ? void 0 : onError();
        }
        returnFocus();
    };
    var _useSubscribeMutation = useSubscribeMutation({
        handlers: {
            onSuccess: handleResponse,
            onError: handleError
        }
    }), sendSubscriptionRequest = _useSubscribeMutation[0], loading = _useSubscribeMutation[1].loading;
    var validate = function(value) {
        if (!value.length) {
            setState("empty");
            dispatchErrorAnalyticsEvent("client", "NOT_VALID_EMPTY");
            return false;
        }
        var isValidEmail = !!value.match(emailRegExp);
        if (!isValidEmail) {
            setState("pattern");
            dispatchErrorAnalyticsEvent("client", "NOT_VALID_PATTERN");
            return false;
        }
        setState("init");
        return true;
    };
    var handleChange = function(event) {
        setState("init");
        setInputValue(event.target.value);
    };
    var handleSubmit = function(event) {
        event.preventDefault();
        if (validate(inputValue)) {
            sendSubscriptionRequest(_extends({
                email: inputValue
            }, service));
        } else {
            returnFocus();
        }
    };
    var handleBlur = function() {
        if (inputValue.length > 1) {
            validate(inputValue);
        }
    };
    var handleFocus = function() {
        if (!ignoreFocusRef.current) {
            setState("init");
        } else {
            ignoreFocusRef.current = false;
        }
    };
    var isInitialState = state === "init";
    var isSucceed = state === "success";
    var isError = [
        "unexpected",
        "pattern",
        "alreadySubscribed"
    ].includes(state);
    var inputHint = !isInitialState && !isSucceed ? messages[state] : undefined;
    var agreement = useMemo(function() {
        return messages.agreement ? compileJsxLinkTemplate(messages.agreement, function(props) {
            return /*#__PURE__*/ jsx(Link, _extends({
                color: "primary",
                size: "xs1"
            }, props, {
                target: "_blank"
            }));
        }) : undefined;
    }, [
        messages.agreement
    ]);
    return /*#__PURE__*/ jsx(FooterSubscriptions, _extends({}, others, {
        buttonProps: {
            disabled: isSucceed,
            loading: loading,
            leftSide: isSucceed ? /*#__PURE__*/ jsx(StatusCheckLinear, {
                size: 20
            }) : /*#__PURE__*/ jsx(ArrowArrowRight, {
                size: 20
            })
        },
        checkboxProps: {
            disabled: loading || isSucceed
        },
        textFieldProps: {
            state: isError ? "error" : undefined,
            mountIcon: false,
            hint: inputHint,
            disabled: loading || isSucceed,
            placeholder: isSucceed ? messages.success : messages.placeholder,
            value: inputValue,
            onBlur: handleBlur,
            onChange: handleChange,
            onFocus: handleFocus
        },
        agreed: agreed,
        agreement: agreement,
        textFieldRef: textFieldRef,
        onAgreementChange: setAgreed,
        onSubmit: handleSubmit
    }));
};

export { SubscriptionsContainer };
